export interface QuantArcBacktestRow {
    time: string;
    decision?: number | null;
    position?: number | null;
    fill_price?: number | null;
    market_price?: number | null;
    trade_pnl?: number | null;
    entry_price?: number | null;
    exit_price?: number | null;
    entry_time?: string | null;
    exit_time?: string | null;
    value?: number | null; // Cumulative PnL from backend
}

export interface QuantArcDerivedPoint extends QuantArcBacktestRow {
    decision: number;
    position: number;
    fill_price: number | null;
    market_price: number | null;
    pnl: number;
    entryMarker: number | null;
    exitMarker: number | null;
    flipMarker: number | null;
    trade_pnl: number | null;
    entry_price: number | null;
    exit_price: number | null;
    entry_time: string | null;
    exit_time: string | null;
}

function toFiniteNumber(value: unknown): number | null {
    if (value == null) return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
}

function classifyEvent(previousPosition: number, nextPosition: number, decision: number): 'entry' | 'exit' | 'flip' | null {
    if (decision === 0 && previousPosition === nextPosition) return null;
    if (previousPosition === 0 && nextPosition !== 0) return 'entry';
    if (previousPosition !== 0 && nextPosition === 0) return 'exit';
    if (previousPosition !== 0 && nextPosition !== 0 && Math.sign(previousPosition) !== Math.sign(nextPosition)) {
        return 'flip';
    }
    if (Math.abs(nextPosition) > Math.abs(previousPosition)) return 'entry';
    if (Math.abs(nextPosition) < Math.abs(previousPosition)) return 'exit';
    return null;
}

export function deriveQuantArcBacktestSeries(rows: QuantArcBacktestRow[] | null | undefined): QuantArcDerivedPoint[] {
    if (!rows?.length) return [];

    const sortedRows = [...rows].sort((a, b) => a.time.localeCompare(b.time));
    const points: QuantArcDerivedPoint[] = [];

    let cash = 0;
    let previousPosition = 0;
    let previousMarketPrice: number | null = null;

    for (const row of sortedRows) {
        const decision = toFiniteNumber(row.decision) ?? 0;
        const fillPrice = toFiniteNumber(row.fill_price);
        const reportedPosition = toFiniteNumber(row.position);
        const marketPrice: number | null = toFiniteNumber(row.market_price) ?? fillPrice ?? previousMarketPrice;
        const nextPosition = reportedPosition ?? previousPosition + decision;

        if (decision !== 0 && fillPrice != null) {
            cash -= decision * fillPrice;
        }

        const mtmPrice = marketPrice ?? 0;
        const pnl = cash + nextPosition * mtmPrice;
        
        // If the backend provided a cumulative value, we can use it or fall back to our calculation
        const pointPnl = toFiniteNumber(row['value']) ?? pnl;

        const event = classifyEvent(previousPosition, nextPosition, decision);
        
        // Use explicit entry/exit prices if available from the backend
        const ep = toFiniteNumber(row.entry_price);
        const xp = toFiniteNumber(row.exit_price);
        const markerPrice = marketPrice ?? fillPrice;

        points.push({
            time: row.time,
            decision,
            position: nextPosition,
            fill_price: fillPrice,
            market_price: marketPrice,
            pnl: pointPnl,
            entryMarker: event === 'entry' ? (ep ?? markerPrice) : null,
            exitMarker: event === 'exit' ? (xp ?? markerPrice) : null,
            flipMarker: event === 'flip' ? markerPrice : null,
            trade_pnl: toFiniteNumber(row.trade_pnl),
            entry_price: ep,
            exit_price: xp,
            entry_time: row.entry_time ?? null,
            exit_time: row.exit_time ?? null,
        });

        previousPosition = nextPosition;
        previousMarketPrice = marketPrice;
    }

    return points;
}
